import React, { useCallback, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import {
  selectCurrentBoardData,
  selectFilters,
  setSearchQuery,
  setSort,
  setStatusFilters,
} from "store/currentBoardSlice";
import { openModal } from "store/modalsSlice";

import { ReactComponent as CodeSvg } from "assets/icons/code-download.svg";
import { ReactComponent as PublicSvg } from "assets/icons/globe2.svg";
import Button from "layout/atoms/Button";
import GridBoard from "layout/atoms/GridBoard";
import Icon from "layout/atoms/Icon";
import Loader from "layout/atoms/Loader";
import MainContainer from "layout/atoms/MainContainer";
import EmptyState from "layout/molecules/EmptyState";
import Filters from "layout/molecules/Filters";

import IdeaCard from "layout/organisms/IdeaCard";

import { ReactComponent as GridSvg } from "assets/icons/grid.svg";
import { ReactComponent as ListSvg } from "assets/icons/list.svg";
import { ReactComponent as PlusSquareSVG } from "assets/icons/plus-square.svg";
import Alert from "layout/atoms/Alert";
import ContentBox from "layout/atoms/ContentBox";
import Dropdown from "layout/atoms/Dropdown";
import InputCheckbox from "layout/atoms/InputCheckbox";
import InputText from "layout/atoms/InputText";
import Label from "layout/atoms/Label";

const STATUS_ORDER = [
  "OPEN",
  "REVIEW",
  "PLANNED",
  "IN_PROGRESS",
  "COMPLETE",
  "DECLINED",
];

const IdeasList = React.memo(
  ({
    ideas,
    statusFilters,
    sortName,
    searchQuery,
    viewMode,
    boardId,
    onOpenSettings,
    t,
  }) => {
    const sorted = useMemo(() => {
      const filtered = ideas.filter((idea) =>
        statusFilters.includes(idea.status),
      );

      const searched = searchQuery
        ? filtered.filter((idea) =>
            idea.title.toLowerCase().includes(searchQuery.toLowerCase()),
          )
        : filtered;

      return searched.slice().sort((a, b) => {
        switch (sortName) {
          case "VOTES":
            return b.voteCount - a.voteCount;
          case "STATUS":
            return (
              STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status)
            );
          case "DATE_DESC":
            return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
          case "DATE_ASC":
            return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
          default:
            return b.voteCount - a.voteCount;
        }
      });
    }, [ideas, statusFilters, sortName, searchQuery]);

    if (sorted.length > 0) {
      return (
        <div>
          {sorted.map((idea) => (
            <IdeaCard
              key={idea.id}
              ideaId={idea.id}
              boardId={boardId}
              viewMode={viewMode}
            />
          ))}
        </div>
      );
    }

    if (ideas.length === 0) {
      return (
        <>
          <ContentBox>
            <h3>{t("emptyStates.ideas.help.title")}</h3>
            <p style={{ marginTop: "10px", marginBottom: "10px" }}>
              {t("emptyStates.ideas.help.question")}
            </p>
            <p style={{ marginTop: "10px", marginBottom: "10px" }}>
              <Icon src={CodeSvg} />
              <Button onClick={() => onOpenSettings("widget")}>
                {t("emptyStates.ideas.help.embedOption")}
              </Button>
            </p>
            <p style={{ marginTop: "10px", marginBottom: "10px" }}>
              <Icon src={PublicSvg} />
              <Button onClick={() => onOpenSettings("settings")}>
                {t("emptyStates.ideas.help.publicOption")}
              </Button>
            </p>
          </ContentBox>
          <EmptyState
            type="noIdeas"
            header={t("emptyStates.ideas.header")}
            subheader={t("emptyStates.ideas.subheader")}
          />
        </>
      );
    }

    return (
      <EmptyState
        type="noResults"
        header={t("boardsDetails.filteredEmptyState.header")}
        subheader={t("boardsDetails.filteredEmptyState.subheader")}
      />
    );
  },
);

function BoardDetails() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { boardId } = useParams();
  const currentBoardData = useSelector(selectCurrentBoardData);
  const { statusFilters, sortName, searchQuery } = useSelector(selectFilters);
  const [viewMode, setViewMode] = useState(() => {
    try {
      const prefs = JSON.parse(localStorage.getItem("ideoloUserPrefs") || "{}");
      return prefs?.boardview?.[boardId] || "normal";
    } catch (e) {
      return "normal";
    }
  });

  const navigate = useNavigate();

  const openSettings = useCallback(
    (path) => {
      navigate(`/admin/boards/${boardId}/${path}`);
    },
    [navigate, boardId],
  );

  const handleViewModeChange = useCallback(
    (mode) => {
      setViewMode(mode);
      try {
        const prefs = JSON.parse(
          localStorage.getItem("ideoloUserPrefs") || "{}",
        );
        if (!prefs.boardview) prefs.boardview = {};
        prefs.boardview[boardId] = mode;
        localStorage.setItem("ideoloUserPrefs", JSON.stringify(prefs));
      } catch (e) {
        console.error("Failed to save view mode preference", e);
      }
    },
    [boardId],
  );

  const cappedIdeaExist = () => currentBoardData.limits.cappedIdeas > 0;

  const statuses = [
    { name: "Unpublished", value: "NOT_PUBLIC" },
    { name: "Open", value: "OPEN" },
    { name: "Review", value: "REVIEW" },
    { name: "Planned", value: "PLANNED" },
    { name: "In Progress", value: "IN_PROGRESS" },
    { name: "Complete", value: "COMPLETE" },
    { name: "Declined", value: "DECLINED" },
  ];

  const allStatusValues = statuses.map((s) => s.value);
  const allExceptDeclined = allStatusValues.filter((v) => v !== "DECLINED");
  const allExceptDeclinedSelected = allExceptDeclined.every((v) =>
    statusFilters.includes(v),
  );
  const allSelected = allStatusValues.every((v) => statusFilters.includes(v));

  const handleToggleStatus = (value) => {
    const newFilters = statusFilters.includes(value)
      ? statusFilters.filter((s) => s !== value)
      : [...statusFilters, value];
    dispatch(setStatusFilters(newFilters));
  };

  const handleToggleAll = () => {
    dispatch(
      setStatusFilters(allExceptDeclinedSelected ? [] : allExceptDeclined),
    );
  };

  const triggerLabel = allSelected
    ? t("statuses.ALL")
    : allExceptDeclinedSelected
      ? t("statuses.ALL_EXCEPT_DECLINED")
      : statusFilters.length === 1
        ? t(`statuses.${statusFilters[0]}`)
        : statusFilters.length > 1
          ? t("statuses.nSelected", { n: statusFilters.length })
          : "—";

  const sorts = [
    { name: "Votes", value: "VOTES" },
    { name: "Status", value: "STATUS" },
    { name: "Date (Newest)", value: "DATE_DESC" },
    { name: "Date (Oldest)", value: "DATE_ASC" },
  ];

  const statusCounts = useMemo(() => {
    const counts = {};
    for (const idea of currentBoardData.ideas.ideas) {
      counts[idea.status] = (counts[idea.status] || 0) + 1;
    }
    return counts;
  }, [currentBoardData.ideas.ideas]);

  const filteredCount = useMemo(() => {
    const ideas = currentBoardData.ideas.ideas;
    const filtered = ideas.filter((idea) => statusFilters.includes(idea.status));
    const searched = searchQuery
      ? filtered.filter((idea) =>
          idea.title.toLowerCase().includes(searchQuery.toLowerCase()),
        )
      : filtered;
    return searched.length;
  }, [currentBoardData.ideas.ideas, statusFilters, searchQuery]);

  return (
    <MainContainer
      topbar={
        <>
          <div>
            <div>
              <h2>{t("boardsDetails.submenuNav.ideasList")}</h2>
            </div>
            <div>
              <InputText
                search
                name="search"
                placeholder={t("boardsDetails.submenuNav.searchPlaceholder")}
                value={searchQuery}
                onChange={(e) => dispatch(setSearchQuery(e.target.value))}
              />
              <Button
                size="lg"
                color="primary"
                onClick={() =>
                  dispatch(
                    openModal({ name: "addIdeaModal", data: { boardId } }),
                  )
                }
              >
                <Icon src={PlusSquareSVG} />
                <p>{t("boardsDetails.submenuNav.addNewIdea")}</p>
              </Button>
            </div>
          </div>
          <Filters
            count={filteredCount}
            search={searchQuery || undefined}
            onClearSearch={() => dispatch(setSearchQuery(""))}
            filters={
              <Dropdown
                placement="bottom-start"
                closeOnClick={false}
                trigger={<strong>{triggerLabel} &#9662;</strong>}
              >
                <InputCheckbox
                  checked={allExceptDeclinedSelected}
                  onChange={handleToggleAll}
                >
                  <strong>{t("statuses.ALL_EXCEPT_DECLINED")}</strong>
                </InputCheckbox>
                {statuses.map((item) => (
                  <InputCheckbox
                    key={item.value}
                    checked={statusFilters.includes(item.value)}
                    onChange={() => handleToggleStatus(item.value)}
                  >
                    <Label size="s" status={item.value}>
                      {t(`statuses.${item.value}`)}
                    </Label>
                    {statusCounts[item.value] != null && (
                      <span style={{ marginLeft: 6, fontSize: 11, color: "#aaa" }}>
                        {statusCounts[item.value]}
                      </span>
                    )}
                  </InputCheckbox>
                ))}
              </Dropdown>
            }
            sorts={
              <Dropdown
                placement="bottom-start"
                trigger={<strong>{t(`sorts.${sortName}`)} &#9662;</strong>}
              >
                {sorts.map((item) => (
                  <Button
                    key={item.value}
                    size="sm"
                    color={sortName === item.value ? "light" : "white"}
                    onClick={() => dispatch(setSort(item.value))}
                  >
                    {t(`sorts.${item.value}`)}
                  </Button>
                ))}
              </Dropdown>
            }
            viewMode={
              <>
                {viewMode === "normal" ? (
                  <Button
                    size="sm-square"
                    color="white"
                    onClick={() => handleViewModeChange("compact")}
                  >
                    <Icon src={ListSvg} />
                  </Button>
                ) : (
                  <Button
                    size="sm-square"
                    color="white"
                    onClick={() => handleViewModeChange("normal")}
                  >
                    <Icon src={GridSvg} />
                  </Button>
                )}
              </>
            }
          />
        </>
      }
      topbarWithBottomBar
    >
      {cappedIdeaExist() && (
        <Alert type="warning">
          <Trans
            i18nKey="boardsDetails.hiddenIdeasPopup"
            values={{ cappedIdeas: currentBoardData.limits.cappedIdeas }}
          >
            There are
            <NavLink to="/admin/subscription">Upgrade your plan</NavLink>
            to see them
          </Trans>
        </Alert>
      )}
      <GridBoard>
        {currentBoardData.ideas.status.fetchIdeas === "loading" ? (
          <Loader />
        ) : (
          <IdeasList
            ideas={currentBoardData.ideas.ideas}
            statusFilters={statusFilters}
            sortName={sortName}
            searchQuery={searchQuery}
            viewMode={viewMode}
            boardId={boardId}
            onOpenSettings={openSettings}
            t={t}
          />
        )}
      </GridBoard>
    </MainContainer>
  );
}

export default BoardDetails;
